import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStableAccessToken } from '@app/GranularDeploy/hooks/useStableAccessToken';
import {
  apiPreviewDeploy,
  apiCheckJob,
  apiJenkinsBuildStatus,
  apiConfirmSession,
  apiSessionStatus,
  SessionStatusRecord,
  PreviewService,
} from '../helpers/apiHelpers';

type SessionStatus = SessionStatusRecord['status'] | 'timeout' | null;

// How long to poll for the session to become 'running' before giving up (25 min).
const SESSION_CONFIRM_TIMEOUT_MS = 25 * 60 * 1000;
const SESSION_POLL_INTERVAL_MS = 15 * 1000;  // check Jenkins every 15s
const SESSION_DB_POLL_INTERVAL_MS = 30 * 1000; // check DB status every 30s (fallback)
// How long to wait for the queued Jenkins build to get a job URL before giving up (5 min).
const QUEUE_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const QUEUE_POLL_INTERVAL_MS = 2500;

const STORAGE_KEY = 'preview-job-status';

// Only the fields needed to resume session-status polling (and render the widget)
// survive a page reload. The trigger/queue-wait phase (isDeploying, statusMessage)
// is tied to an in-memory queueId and can't be resumed after a refresh.
interface PersistedJobStatus {
  namespace: string;
  previewTag: string;
  ttl: string;
  jobUrl: string | null;
  sessionStatus: SessionStatus;
  deployError: string | null;
  timeoutReason: string | null;
  dismissed: boolean;
}

function loadPersisted(): PersistedJobStatus | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function savePersisted(state: PersistedJobStatus) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore quota/serialization errors */ }
}

interface PreviewJobStatusContextType {
  isDeploying: boolean;
  jobUrl: string | null;
  deployError: string | null;
  statusMessage: string;
  sessionStatus: SessionStatus;
  timeoutReason: string | null;
  namespace: string;
  previewTag: string;
  dismissed: boolean;
  dismiss: () => void;
  deploy: (payload: {
    environment: string;
    namespace: string;
    previewKey: string;
    ttl: string;
    services: PreviewService[];
    infra?: PreviewService[];
    dbBranch?: string;
    force?: boolean;
    companionsConsidered?: string[];
  }) => Promise<void>;
}

const PreviewJobStatusContext = createContext<PreviewJobStatusContextType | undefined>(undefined);

const isTerminal = (status: SessionStatus, deployError: string | null) =>
  deployError !== null || status === 'running' || status === 'failed' || status === 'timeout';

export const PreviewJobStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tokenGetter = useStableAccessToken();

  const persisted = useRef(loadPersisted()).current;

  const [isDeploying, setIsDeploying] = useState(false);
  const [jobUrl, setJobUrl] = useState<string | null>(persisted?.jobUrl ?? null);
  const [deployError, setDeployError] = useState<string | null>(persisted?.deployError ?? null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>(persisted?.sessionStatus ?? null);
  const [timeoutReason, setTimeoutReason] = useState<string | null>(persisted?.timeoutReason ?? null);
  const [namespace, setNamespace] = useState<string>(persisted?.namespace ?? '');
  const [previewTag, setPreviewTag] = useState<string>(persisted?.previewTag ?? '');
  const [ttl, setTtl] = useState<string>(persisted?.ttl ?? '');
  const [dismissed, setDismissed] = useState<boolean>(persisted?.dismissed ?? true);

  const pollAbort = useRef<AbortController | null>(null);

  const startSessionPoller = useCallback((ns: string, tag: string, url: string, ttlValue: string) => {
    pollAbort.current?.abort();
    const abort = new AbortController();
    pollAbort.current = abort;
    const deadline = Date.now() + SESSION_CONFIRM_TIMEOUT_MS;
    let resolved = false;
    let lastPollError: string | null = null;

    const resolve = (status: SessionStatus, reason?: string | null) => {
      if (resolved) return;
      resolved = true;
      abort.abort();
      setSessionStatus(status);
      if (reason) setTimeoutReason(reason);
    };

    const isAuthError = (err: any) => err?.status === 401 || err?.status === 403;

    const jenkinsLoop = async () => {
      while (!abort.signal.aborted && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, SESSION_POLL_INTERVAL_MS));
        if (abort.signal.aborted) break;
        try {
          const build = await apiJenkinsBuildStatus(url, tokenGetter);
          if (build.result === 'SUCCESS') {
            try { await apiConfirmSession(ns, tag, ttlValue, tokenGetter); } catch { /* non-fatal */ }
            resolve('running');
            return;
          }
          if (build.result === 'FAILURE' || build.result === 'ABORTED' || build.result === 'UNSTABLE') {
            resolve('failed');
            return;
          }
        } catch (err: any) {
          if (isAuthError(err)) {
            resolve('failed', 'Your session expired while checking the build status. Please sign in again and retry.');
            return;
          }
          // transient network error — keep polling, but remember it in case we time out
          lastPollError = err?.message || 'Unknown error';
        }
      }
      if (!abort.signal.aborted && Date.now() >= deadline) {
        resolve('timeout', lastPollError ? `Still waiting after 25 minutes. Last error while polling: ${lastPollError}` : null);
      }
    };

    const dbLoop = async () => {
      await new Promise((r) => setTimeout(r, SESSION_DB_POLL_INTERVAL_MS));
      while (!abort.signal.aborted) {
        try {
          const record = await apiSessionStatus(ns, tag, tokenGetter);
          if (record?.status === 'running') { resolve('running'); return; }
          if (record?.status === 'failed')  { resolve('failed');  return; }
        } catch (err: any) {
          if (isAuthError(err)) {
            resolve('failed', 'Your session expired while checking the deploy status. Please sign in again and retry.');
            return;
          }
          lastPollError = err?.message || 'Unknown error'; // non-fatal, keep polling
        }
        await new Promise((r) => setTimeout(r, SESSION_DB_POLL_INTERVAL_MS));
      }
    };

    jenkinsLoop();
    dbLoop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume polling once on mount if a previous browser session left an
  // unresolved job (e.g. the tab was reloaded while a preview was still starting).
  useEffect(() => {
    if (persisted?.jobUrl && !isTerminal(persisted.sessionStatus, persisted.deployError)) {
      startSessionPoller(persisted.namespace, persisted.previewTag, persisted.jobUrl, persisted.ttl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist whenever the resumable/displayable fields change.
  useEffect(() => {
    savePersisted({ namespace, previewTag, ttl, jobUrl, sessionStatus, deployError, timeoutReason, dismissed });
  }, [namespace, previewTag, ttl, jobUrl, sessionStatus, deployError, timeoutReason, dismissed]);

  const dismiss = useCallback(() => setDismissed(true), []);

  const deploy = useCallback(async (payload: {
    environment: string;
    namespace: string;
    previewKey: string;
    ttl: string;
    services: PreviewService[];
    infra?: PreviewService[];
    dbBranch?: string;
    force?: boolean;
    companionsConsidered?: string[];
  }) => {
    setIsDeploying(true);
    setJobUrl(null);
    setDeployError(null);
    setSessionStatus(null);
    setTimeoutReason(null);
    setStatusMessage('Triggering preview deploy job…');
    setNamespace(payload.namespace);
    setPreviewTag(payload.previewKey);
    setTtl(payload.ttl);
    setDismissed(false);

    try {
      const response = await apiPreviewDeploy(payload, tokenGetter);

      if (response.status === 'triggered') {
        const url = (response as any).jobUrl as string;
        setJobUrl(url);
        setIsDeploying(false);
        setStatusMessage('');
        startSessionPoller(payload.namespace, payload.previewKey, url, payload.ttl);
        return;
      }

      // queued — poll until we get the URL
      const queueId = (response as any).id;
      setStatusMessage('Fetching Jenkins job URL. This may take a moment…');

      const queueDeadline = Date.now() + QUEUE_POLL_TIMEOUT_MS;
      let queueResolved = false;
      while (Date.now() < queueDeadline) {
        const item = await apiCheckJob(queueId, tokenGetter);
        if (item.status === 'triggered') {
          const url = (item as any).jobUrl as string;
          setJobUrl(url);
          setIsDeploying(false);
          setStatusMessage('');
          startSessionPoller(payload.namespace, payload.previewKey, url, payload.ttl);
          queueResolved = true;
          break;
        } else if (item.status === 'cancelled') {
          setDeployError('The job was cancelled.');
          setIsDeploying(false);
          queueResolved = true;
          break;
        } else if (item.status === 'unknown') {
          setDeployError('The job status is unknown.');
          setIsDeploying(false);
          queueResolved = true;
          break;
        } else if (item.status === 'blocked') {
          setStatusMessage(`Job is pending. Reason: ${(item as any).reason}`);
          await new Promise((r) => setTimeout(r, QUEUE_POLL_INTERVAL_MS));
        } else {
          await new Promise((r) => setTimeout(r, QUEUE_POLL_INTERVAL_MS));
        }
      }
      if (!queueResolved) {
        setDeployError('Timed out waiting for the Jenkins job to start.');
        setIsDeploying(false);
      }
    } catch (err: any) {
      setDeployError(err.message || 'Failed to trigger deploy job');
      setIsDeploying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenGetter, startSessionPoller]);

  const value = useMemo(() => ({
    isDeploying,
    jobUrl,
    deployError,
    statusMessage,
    sessionStatus,
    timeoutReason,
    namespace,
    previewTag,
    dismissed,
    dismiss,
    deploy,
  }), [isDeploying, jobUrl, deployError, statusMessage, sessionStatus, timeoutReason, namespace, previewTag, dismissed, dismiss, deploy]);

  return (
    <PreviewJobStatusContext.Provider value={value}>
      {children}
    </PreviewJobStatusContext.Provider>
  );
};

export const usePreviewJobStatus = (): PreviewJobStatusContextType => {
  const context = useContext(PreviewJobStatusContext);
  if (context === undefined) {
    throw new Error('usePreviewJobStatus must be used within a PreviewJobStatusProvider');
  }
  return context;
};
